import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

// Interactive countdown control. Backs the `type: 'countdown'` variable
// (currently used by the Starting Soon overlay). Value is a server-time
// epoch ms (or null = no active countdown). Calls `onChange(newEndsAt)`
// when the user clicks Start or Clear.
//
// `serverOffset` is `serverNow - localNow` captured at config-load time
// in PackageProduction; we add it to local Date.now() so the displayed
// remaining time and the stored endsAt agree with what the OBS overlay
// (which compares against server time) renders.
export default function CountdownField({
  value,
  onChange,
  serverOffset = 0,
  disabled = false,
}) {
  const [mins, setMins] = useState('');

  // 1-second tick to refresh the "remaining" display while a countdown
  // is active. Cheap (just a re-render).
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (value == null) return undefined;
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [value]);

  const serverNow = Date.now() + serverOffset;
  const active = value != null && value > serverNow;
  const remaining = active ? Math.ceil((value - serverNow) / 1000) : 0;
  const mm = Math.floor(remaining / 60);
  const ss = String(remaining % 60).padStart(2, '0');

  // Start / Clear are discrete clicks — pass `immediate: true` so the
  // production page bypasses its debounced autosave and the OBS overlay
  // picks the new endsAt up on its next poll without an extra 1.2 s of
  // debounce latency.
  function start() {
    const n = parseInt(mins, 10);
    if (!Number.isFinite(n) || n < 1) return;
    onChange(serverNow + n * 60 * 1000, { immediate: true });
    setMins('');
  }

  function clear() {
    onChange(null, { immediate: true });
  }

  if (active) {
    return (
      <div
        className="text-center p-3"
        style={{ background: 'rgba(0,212,255,0.06)', borderRadius: 6 }}
      >
        <div
          style={{
            fontSize: 32,
            fontWeight: 700,
            color: 'var(--neon-blue)',
            lineHeight: 1,
          }}
        >
          {mm}:{ss}
        </div>
        <div className="text-muted small mb-2">remaining</div>
        <button
          type="button"
          className="btn-ghost btn-ghost--danger w-100 justify-content-center"
          disabled={disabled}
          onClick={clear}
        >
          <Clock size={14} aria-hidden="true" />
          Clear countdown
        </button>
      </div>
    );
  }

  return (
    <div className="d-flex gap-2 align-items-stretch">
      <div className="input-group" style={{ flex: 1 }}>
        <input
          type="number"
          className="form-control"
          min="1"
          placeholder="e.g. 5"
          value={mins}
          onChange={(e) => setMins(e.target.value)}
          disabled={disabled}
        />
        <span className="input-group-text">min</span>
      </div>
      <button
        type="button"
        className="btn-ghost btn-ghost--primary justify-content-center"
        style={{ whiteSpace: 'nowrap' }}
        onClick={start}
        disabled={
          disabled
          || !Number.isFinite(parseInt(mins, 10))
          || parseInt(mins, 10) < 1
        }
      >
        <Clock size={14} aria-hidden="true" />
        Start
      </button>
    </div>
  );
}
